
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { MessageSquare, Send, Bot, User, Trash2, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { AIAnalysisResult } from "@/hooks/useAIAnalysis";
import { useTranslation } from "react-i18next";

// Map i18n language codes to full language names for the AI
const getLanguageName = (code: string): string => {
  const languageMap: Record<string, string> = {
    'fr': 'French',
    'en': 'English',
    'es': 'Spanish'
  };
  return languageMap[code] || 'French';
};

interface ChatMessage {
  id: string;
  type: 'user' | 'ai';
  content: string;
  timestamp: Date;
}

interface AIQuestionChatProps {
  analysisResult: AIAnalysisResult;
}

const AIQuestionChat = ({ analysisResult }: AIQuestionChatProps) => {
  const { t, i18n } = useTranslation('ai-analysis');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [question, setQuestion] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  // Charger l'historique depuis localStorage au montage
  useEffect(() => {
    const savedMessages = localStorage.getItem('ai-chat-history');
    if (savedMessages) {
      try {
        const parsedMessages = JSON.parse(savedMessages);
        // Convertir les timestamps en objets Date
        const messagesWithDates = parsedMessages.map((msg: any) => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }));
        setMessages(messagesWithDates);
      } catch (error) {
        console.error('Erreur lors du chargement de l\'historique:', error);
      }
    }
  }, []);

  // Sauvegarder l'historique dans localStorage à chaque changement
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem('ai-chat-history', JSON.stringify(messages));
    }
  }, [messages]);

  const suggestedQuestions = [
    t('chat.suggestions.question1'),
    t('chat.suggestions.question2'), 
    t('chat.suggestions.question3'),
    t('chat.suggestions.question4')
  ];

  const sendQuestion = async (questionText: string) => {
    if (!questionText.trim()) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: questionText,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setQuestion('');
    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('ai-chat', {
        body: {
          question: questionText,
          analysisContext: analysisResult,
          language: getLanguageName(i18n.language)
        }
      });

      if (error) throw error;

      const aiMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        content: data.response,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error: any) {
      console.error('Erreur lors de l\'envoi de la question:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible d'obtenir une réponse. Veuillez réessayer.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendQuestion(question);
  };

  const clearAllHistory = () => {
    setMessages([]);
    localStorage.removeItem('ai-chat-history');
    toast({
      title: "Historique effacé",
      description: "Toutes les conversations ont été supprimées.",
    });
  };

  const deleteMessage = (messageId: string) => {
    setMessages(prev => {
      const newMessages = prev.filter(msg => msg.id !== messageId);
      if (newMessages.length === 0) {
        localStorage.removeItem('ai-chat-history');
      } else {
        localStorage.setItem('ai-chat-history', JSON.stringify(newMessages));
      }
      return newMessages;
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-golf-600" />
            {t('chat.title')}
          </div>
          {messages.length > 0 && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" className="text-destructive hover:text-destructive">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t('chat.clearHistory')}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t('chat.clearConfirmDescription')}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('chat.cancel')}</AlertDialogCancel>
                  <AlertDialogAction onClick={clearAllHistory} className="bg-destructive hover:bg-destructive/90">
                    {t('chat.confirm')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Messages d'historique */}
        {messages.length > 0 && (
          <div className="space-y-3 max-h-60 overflow-y-auto">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`group flex gap-3 ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`flex gap-2 max-w-[80%] ${message.type === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    message.type === 'user' ? 'bg-golf-600 text-white' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {message.type === 'user' ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
                  </div>
                  <div className={`relative p-3 rounded-lg ${
                    message.type === 'user' 
                      ? 'bg-golf-600 text-white' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className={`absolute -top-2 -right-2 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity ${
                            message.type === 'user' ? 'text-white hover:bg-white/20' : 'text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Supprimer ce message</AlertDialogTitle>
                          <AlertDialogDescription>
                            Êtes-vous sûr de vouloir supprimer ce message ? Cette action ne peut pas être annulée.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Annuler</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={() => deleteMessage(message.id)}
                            className="bg-destructive hover:bg-destructive/90"
                          >
                            Supprimer
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Questions suggérées */}
        {messages.length === 0 && (
          <div className="space-y-2">
            <p className="text-sm text-gray-600">{t('chat.suggestions.title')}</p>
            <div className="flex flex-wrap gap-2">
              {suggestedQuestions.map((suggested, index) => (
                <Button
                  key={index}
                  variant="outline"
                  size="sm"
                  onClick={() => sendQuestion(suggested)}
                  disabled={isLoading}
                  className="text-xs"
                >
                  {suggested}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Formulaire de saisie */}
        <form onSubmit={handleSubmit} className="flex gap-2">
          <Input
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder={t('chat.placeholder')}
            disabled={isLoading}
            className="flex-1"
          />
          <Button type="submit" disabled={isLoading || !question.trim()}>
            {isLoading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </form>

        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-golf-600" />
            {t('chat.loading')}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AIQuestionChat;
